/**
 * @fileoverview Search box shared by every filterable sales table.
 * @description A bordered box with a leading search icon and a borderless text input, matching
 * the design's filter-bar search field on Active Clients, Sales Pipeline, Sales Team, and User
 * Management.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { ChangeEvent } from 'react'

interface TableSearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  'data-testid'?: string
}

/**
 * @component
 * @param {TableSearchInputProps} props - The search box's current value, change handler, and
 * placeholder.
 * @param {string} props.value - The current search text.
 * @param {(value: string) => void} props.onChange - Called with the new text on every keystroke.
 * @param {string} props.placeholder - The input's placeholder text.
 * @param {string} [props['data-testid']] - QA locator for the text input.
 */
function TableSearchInput({
  value,
  onChange,
  placeholder,
  'data-testid': testId,
}: TableSearchInputProps) {
  /**
   * @description Forwards the input's new text to the caller's onChange.
   * @param {ChangeEvent<HTMLInputElement>} event - The input's change event.
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }

  return (
    <div className="flex min-w-60 flex-1 items-center gap-2 rounded-lg border border-input-border bg-white px-3">
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="shrink-0 text-placeholder"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l4.5 4.5" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        data-testid={testId}
        className="min-w-0 flex-1 border-none bg-transparent py-2.5 text-[13.5px] text-ink outline-none placeholder:text-placeholder"
      />
    </div>
  )
}

export default TableSearchInput
